/**
 * KebabMenu — menú de acciones de tres puntos, con nombre accesible obligatorio.
 *
 * Existe para cerrar #477 (WCAG 4.1.2 "Name, Role, Value"): los kebabs sueltos
 * repartidos por el repo eran `<Dropdown.Toggle>` con un `<i>` adentro y nada
 * más, o sea un botón sin texto accesible. Un lector de pantalla anuncia
 * "botón" y nada más, y en una lista de 30 filas hay 30 botones idénticos sin
 * forma de distinguirlos.
 *
 * `label` es **required a propósito**: es lo único que frena la regresión. Sin
 * un componente compartido cada vista nueva volvía a escribir el kebab a mano
 * y volvía a olvidarse del `aria-label` (así entraron los últimos dos casos,
 * en ClientDetail y ClientIndustriesSettings, después de que se abriera el
 * issue).
 *
 * El label describe LA FILA, no el widget:
 *   ✅ `Acciones de ${project.title}`
 *   ✅ `Acciones de la cotización ${quotation.quotation_number}`
 *   ❌ "Menú", "Acciones", "Opciones"   ← no distinguen una fila de otra
 *
 * Beneficio colateral (#472): con nombre accesible los tests pueden usar
 * `getByRole('button', { name: /Acciones de Proyecto X/ })` en vez de bucear el
 * DOM por clase de CSS, que es lo que dispara `testing-library/no-node-access`
 * y `testing-library/no-container`.
 *
 * @module components/common/KebabMenu
 */

import React from 'react';
import { Dropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * @param {Object}  props
 * @param {string}  props.label            - Nombre accesible. Describe la fila. Requerido.
 * @param {React.ReactNode} props.children - `<Dropdown.Item>`s del menú.
 * @param {string}  [props.align]          - Alineación del menú (default 'end').
 * @param {string}  [props.size]           - Tamaño del toggle ('sm' | 'lg').
 * @param {string}  [props.id]             - id del toggle (útil para tests/labels externos).
 * @param {string}  [props.toggleClassName]- Clases extra del toggle.
 * @param {string}  [props.iconClassName]  - Clases extra del icono (ej. 'fs-5' para el kebab del header).
 * @param {Object}  [props.toggleStyle]    - Estilo inline extra del toggle. Se mergea sobre el default.
 *                                           Uso principal: garantizar el tap-target de 44px en mobile
 *                                           (`{ minHeight: 44, minWidth: 44 }`).
 * @param {string}  [props.className]      - Clases extra del `<Dropdown>`.
 * @param {boolean} [props.disabled]       - Deshabilita el toggle.
 * @param {boolean} [props.stopPropagation]- Frena el click hacia la fila/card (default true).
 * @param {Object}  [props.menuProps]      - Props extra para `<Dropdown.Menu>`.
 * @param {Object}  [props.dropdownProps]  - Props extra para `<Dropdown>`.
 */
const KebabMenu = ({
  label,
  children,
  align = 'end',
  size,
  id,
  toggleClassName = '',
  iconClassName = '',
  toggleStyle,
  className = '',
  disabled = false,
  stopPropagation = true,
  menuProps = {},
  dropdownProps = {},
}) => {
  const handleClick = stopPropagation ? (e) => e.stopPropagation() : undefined;

  return (
    <Dropdown
      align={align}
      className={className}
      onClick={handleClick}
      {...dropdownProps}
    >
      <Dropdown.Toggle
        variant="link"
        size={size}
        id={id}
        disabled={disabled}
        aria-label={label}
        title={label}
        className={`kebab-menu-toggle p-0 text-muted border-0 ${toggleClassName}`.trim()}
        style={{ boxShadow: 'none', ...toggleStyle }}
      >
        <i className={`bi bi-three-dots-vertical ${iconClassName}`.trim()} aria-hidden="true"></i>
      </Dropdown.Toggle>
      <Dropdown.Menu {...menuProps}>{children}</Dropdown.Menu>
    </Dropdown>
  );
};

KebabMenu.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  align: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.string,
  id: PropTypes.string,
  toggleClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  toggleStyle: PropTypes.object,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  stopPropagation: PropTypes.bool,
  menuProps: PropTypes.object,
  dropdownProps: PropTypes.object,
};

export default KebabMenu;
